import React, { useState, useEffect, useCallback } from 'react';
import { getCurrencySymbol, formatCurrency, formatCompactCurrency } from '../utils/currency';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  CheckCircle2, 
  AlertCircle, 
  FileText, 
  Receipt,
  Tag,
  Link2,
  Loader2,
  ThumbsUp,
  Sparkles,
  Search,
  HelpCircle,
  ArrowRight,
  Building,
  ShoppingCart,
  Briefcase,
  Home,
  Car,
  Zap,
  CreditCard,
  Users,
  RefreshCw,
  X,
  Check
} from 'lucide-react';

const TransactionReviewPage = () => {
  const { business } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [loadingAi, setLoadingAi] = useState({});
  const [customSearch, setCustomSearch] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [showCustomInput, setShowCustomInput] = useState({});
  const [chartOfAccounts, setChartOfAccounts] = useState([]);

  // Smart category suggestions with icons (display purposes only)
  const smartCategories = {
    credit: [
      { name: 'Sales Revenue', icon: ShoppingCart, description: 'Payment from a customer' },
      { name: 'Service Revenue', icon: Briefcase, description: 'Payment for services rendered' },
      { name: 'Investment Income', icon: CreditCard, description: 'Returns from investments' },
      { name: 'Loan Received', icon: Building, description: 'Borrowed funds received' },
      { name: 'Other Income', icon: Zap, description: 'Miscellaneous income' }
    ],
    debit: [
      { name: 'Rent', icon: Home, description: 'Office or property rent' },
      { name: 'Utilities', icon: Zap, description: 'Electricity, water, internet' },
      { name: 'Salaries', icon: Users, description: 'Staff wages and salaries' },
      { name: 'Supplies', icon: ShoppingCart, description: 'Office or business supplies' },
      { name: 'Transportation', icon: Car, description: 'Travel and vehicle expenses' },
      { name: 'Professional Services', icon: Briefcase, description: 'Legal, accounting, consulting' },
      { name: 'Equipment', icon: Building, description: 'Business equipment purchase' },
      { name: 'Bank Charges', icon: CreditCard, description: 'Bank fees and charges' }
    ]
  };

  // Fetch Chart of Accounts on mount
  useEffect(() => {
    const fetchCOA = async () => {
      try {
        const response = await axios.get(`${API}/accounting/chart-of-accounts`);
        const accounts = response.data?.accounts || response.data || [];
        setChartOfAccounts(accounts);
      } catch (error) {
        console.error('Failed to fetch COA:', error);
      }
    };
    fetchCOA();
  }, []);

  // Dynamic categories from Chart of Accounts
  const allCategories = chartOfAccounts.map(acc => ({
    code: acc.code,
    name: acc.name,
    type: ['revenue', 'liability', 'equity'].includes(acc.type) ? 'credit' : 'debit',
    keywords: [acc.name.toLowerCase()]
  }));

  const getLocalCurrencySymbol = () => {
    return getCurrencySymbol(business?.currency || 'NGN');
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions/needs-attention`);
      const txns = response.data.transactions || [];
      setTransactions(txns);
      
      // Get AI suggestions for each transaction
      txns.forEach(txn => {
        getAiSuggestion(txn);
      });
    } catch (error) {
      console.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const getAiSuggestion = async (transaction) => {
    setLoadingAi(prev => ({ ...prev, [transaction.id]: true }));
    try {
      const response = await axios.post(`${API}/ai/suggest-category`, {
        description: transaction.description || transaction.narration,
        amount: transaction.amount,
        type: transaction.type,
        date: transaction.date || transaction.posted_date
      });
      
      setAiSuggestions(prev => ({
        ...prev,
        [transaction.id]: response.data
      }));
    } catch (error) {
      // If AI fails, use basic keyword matching
      const suggestion = basicCategorySuggestion(transaction);
      setAiSuggestions(prev => ({
        ...prev,
        [transaction.id]: suggestion
      }));
    } finally {
      setLoadingAi(prev => ({ ...prev, [transaction.id]: false }));
    }
  };

  const basicCategorySuggestion = (transaction) => {
    const desc = (transaction.description || transaction.narration || '').toLowerCase();
    const amount = Math.abs(transaction.amount);
    const isCredit = transaction.type === 'credit' || transaction.amount > 0;
    
    // Simple keyword matching
    const matches = allCategories.filter(cat => {
      const typeMatch = isCredit ? cat.type === 'credit' : cat.type === 'debit';
      const keywordMatch = cat.keywords.some(kw => desc.includes(kw.toLowerCase()));
      return typeMatch && keywordMatch;
    });
    
    if (matches.length > 0) {
      return {
        suggested_category: matches[0].name,
        confidence: 0.6,
        question: `Is this ${matches[0].name.toLowerCase()}?`,
        alternatives: matches.slice(1, 4).map(m => m.name)
      };
    }
    
    return {
      suggested_category: null,
      confidence: 0,
      question: isCredit 
        ? "What type of income is this?"
        : "What is this payment for?",
      alternatives: []
    };
  };

  const searchCategories = (txnId, query) => {
    setCustomSearch(prev => ({ ...prev, [txnId]: query }));
    
    if (query.length < 2) {
      setSearchResults(prev => ({ ...prev, [txnId]: [] }));
      return;
    }
    
    const queryLower = query.toLowerCase();
    const results = allCategories.filter(cat => 
      cat.name.toLowerCase().includes(queryLower) ||
      cat.keywords.some(kw => kw.includes(queryLower))
    ).slice(0, 5);
    
    setSearchResults(prev => ({ ...prev, [txnId]: results }));
  };

  const categorizeTransaction = async (txnId, category, glCode = null) => {
    setProcessing(prev => ({ ...prev, [txnId]: true }));
    try {
      await axios.patch(`${API}/transactions/${txnId}/categorize`, {
        category,
        gl_code: glCode
      });
      toast.success('Transaction categorized!');
      setTransactions(prev => prev.filter(t => t.id !== txnId));
    } catch (error) {
      toast.error('Failed to categorize');
    } finally {
      setProcessing(prev => ({ ...prev, [txnId]: false }));
    }
  };

  const matchToInvoice = async (txnId) => {
    setProcessing(prev => ({ ...prev, [txnId]: true }));
    try {
      const response = await axios.post(`${API}/transactions/${txnId}/match-invoice`);
      if (response.data.matched) {
        toast.success(`Matched to ${response.data.invoice_number}!`);
        setTransactions(prev => prev.filter(t => t.id !== txnId));
      } else {
        toast.info('No matching invoice found. Try categorizing instead.');
      }
    } catch (error) {
      toast.error('Failed to match');
    } finally {
      setProcessing(prev => ({ ...prev, [txnId]: false }));
    }
  };

  const matchToBill = async (txnId) => {
    setProcessing(prev => ({ ...prev, [txnId]: true }));
    try {
      const response = await axios.post(`${API}/transactions/${txnId}/match-bill`);
      if (response.data.matched) {
        toast.success(`Matched to ${response.data.vendor_name}!`);
        setTransactions(prev => prev.filter(t => t.id !== txnId));
      } else {
        toast.info('No matching bill found. Try categorizing instead.');
      }
    } catch (error) {
      toast.error('Failed to match');
    } finally {
      setProcessing(prev => ({ ...prev, [txnId]: false }));
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#108981]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="transaction-review-page">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">AI Transaction Review</h1>
              <p className="text-muted-foreground">Help AI learn how to categorize your transactions</p>
            </div>
          </div>
        </div>

        {transactions.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <ThumbsUp className="w-16 h-16 text-[#108981] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">All caught up!</h3>
              <p className="text-muted-foreground mb-4">
                No transactions need your attention right now. AI has automatically categorized everything.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="bg-[#108981] hover:bg-[#108981]/90">
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                <Badge variant="secondary" className="mr-2">{transactions.length}</Badge>
                transaction(s) need your input
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchTransactions}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
            
            <AnimatePresence>
              {transactions.map((txn, index) => {
                const suggestion = aiSuggestions[txn.id];
                const isCredit = txn.type === 'credit' || txn.amount > 0;
                const quickCategories = isCredit ? smartCategories.credit : smartCategories.debit;
                
                return (
                  <motion.div
                    key={txn.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="overflow-hidden">
                      {/* Transaction Header */}
                      <div className={`p-4 ${isCredit ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge 
                              className={isCredit 
                                ? 'bg-green-100 text-green-700 border-green-200' 
                                : 'bg-red-100 text-red-700 border-red-200'
                              }
                            >
                              {isCredit ? '↓ Money In' : '↑ Money Out'}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {txn.date || txn.posted_date}
                            </span>
                          </div>
                          <span className={`text-2xl font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                            {isCredit ? '+' : '-'}{getLocalCurrencySymbol()}{Math.abs(txn.amount || 0).toLocaleString()}
                          </span>
                        </div>
                        <p className="font-medium text-foreground mt-2 text-lg">
                          {txn.description || txn.narration || 'Unknown transaction'}
                        </p>
                      </div>
                      
                      <CardContent className="p-6 space-y-4">
                        {/* AI Question */}
                        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <HelpCircle className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            {loadingAi[txn.id] ? (
                              <div className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                                <span className="text-blue-800">AI is analyzing this transaction...</span>
                              </div>
                            ) : suggestion?.suggested_category ? (
                              <>
                                <p className="font-medium text-blue-900">
                                  Could this be <span className="text-blue-600">{suggestion.suggested_category}</span>?
                                </p>
                                {suggestion.confidence > 0 && (
                                  <p className="text-sm text-blue-700 mt-1">
                                    AI confidence: {Math.round(suggestion.confidence * 100)}%
                                  </p>
                                )}
                                <div className="flex gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    onClick={() => categorizeTransaction(txn.id, suggestion.suggested_category)}
                                    disabled={processing[txn.id]}
                                    className="bg-blue-600 hover:bg-blue-700 gap-1"
                                  >
                                    <Check className="w-3 h-3" />
                                    Yes, it's {suggestion.suggested_category}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setShowCustomInput(prev => ({ ...prev, [txn.id]: true }))}
                                  >
                                    Something else
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <p className="font-medium text-blue-900">
                                {isCredit ? "What type of income is this?" : "What is this payment for?"}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Quick Category Selection */}
                        {(!suggestion?.suggested_category || showCustomInput[txn.id]) && (
                          <div className="space-y-3">
                            <p className="text-sm font-medium text-muted-foreground">Quick select:</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {quickCategories.map((cat) => {
                                const Icon = cat.icon;
                                return (
                                  <Button
                                    key={cat.name}
                                    variant="outline"
                                    className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-muted/50"
                                    onClick={() => categorizeTransaction(txn.id, cat.name)}
                                    disabled={processing[txn.id]}
                                  >
                                    <Icon className="w-5 h-5 text-muted-foreground" />
                                    <span className="text-xs font-medium">{cat.name}</span>
                                  </Button>
                                );
                              })}
                            </div>
                            
                            {/* Search for other categories */}
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                placeholder="Search for a different category..."
                                value={customSearch[txn.id] || ''}
                                onChange={(e) => searchCategories(txn.id, e.target.value)}
                                className="pl-10"
                              />
                              
                              {/* Search Results */}
                              {searchResults[txn.id]?.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-background border rounded-lg shadow-lg">
                                  {searchResults[txn.id].map((result) => (
                                    <button
                                      key={result.code}
                                      className="w-full px-4 py-3 text-left hover:bg-muted/50 flex items-center justify-between"
                                      onClick={() => {
                                        categorizeTransaction(txn.id, result.name, result.code);
                                        setSearchResults(prev => ({ ...prev, [txn.id]: [] }));
                                      }}
                                    >
                                      <div>
                                        <p className="font-medium">{result.name}</p>
                                        <p className="text-xs text-muted-foreground">GL: {result.code}</p>
                                      </div>
                                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Match to Invoice/Bill */}
                        <div className="flex gap-2 pt-4 border-t">
                          {isCredit && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 gap-2"
                              onClick={() => matchToInvoice(txn.id)}
                              disabled={processing[txn.id]}
                            >
                              {processing[txn.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <FileText className="w-4 h-4" />
                              )}
                              Match to Invoice
                            </Button>
                          )}
                          {!isCredit && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 gap-2"
                              onClick={() => matchToBill(txn.id)}
                              disabled={processing[txn.id]}
                            >
                              {processing[txn.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Receipt className="w-4 h-4" />
                              )}
                              Match to Bill
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TransactionReviewPage;
